import { useEffect, useRef, useState } from 'react';
import Modal from '../../../../../components/Modal';
import Btn from '../../../../../components/Buttons/Btn';
import AnimatedLoadingIcon from '../../../../../components/LoadingOverlay/AnimatedLoadingIcon';
import { getApi } from '../../../../../services/api';
import { loadConfig } from '../../../../../settings';
import { Order } from '../../../../../types/apiResponse/order';
import i18n from '../../../../../i18n';
import { FeedbackCard, FeedbackContainer } from './styles';

const PROTOCOL = 'smartsell-order-editor-v1';
const moduleCache = new Map<string, Promise<{ body: string; status: number; contentType: string }>>();

type Props = {
  order?: Order; sellerCode?: string; onClose: () => void; onSaved: () => void;
  access?: 'edit' | 'view' | 'create'; initialStage?: string;
};
export default function OrderEditorModal({ order, sellerCode, onClose, onSaved, access = 'edit', initialStage }: Props) {
  const displayNumber = order?.history_only ? `ERP ${order.history_order_number}` : order?.NumeroPedido;
  const frame = useRef<HTMLIFrameElement>(null);
  const [channel] = useState(() =>
    Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (value) =>
      value.toString(16).padStart(2, '0'),
    ).join(''),
  );
  const [source, setSource] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirtyRef = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;
  const approvalChanged = useRef(false);
  const finishClose = () => {
    closeRef.current();
    if (approvalChanged.current) {
      approvalChanged.current = false;
      savedRef.current();
    }
  };
  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else finishClose();
  };
  const finishCloseRef = useRef(finishClose);
  finishCloseRef.current = finishClose;

  useEffect(() => {
    let active = true;
    void loadConfig()
      .then((settings) => {
        const configured = settings?.REACT_APP_ORDER_EDITOR_URL;
        const fallback =
          process.env.NODE_ENV === 'development'
            ? `${window.location.protocol}//${window.location.hostname}:3004/editor.html`
            : '/order-editor/editor.html';
        const url = new URL(configured || fallback, window.location.origin);
        url.searchParams.set('parentOrigin', window.location.origin);
        url.searchParams.set('channel', channel);
        if (active) setSource(url.toString());
      })
      .catch(() => {
        if (active) {
          setError('Não foi possível carregar a configuração do editor.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [channel]);

  useEffect(() => {
    if (!source) return;
    let active = true;
    let initialization: Promise<any> | undefined;
    let bootstrap: any;
    const origin = new URL(source).origin;
    const endpoint = access === 'view' ? 'view' : 'editor';
    const historyIdentity = access === 'view' && order?.history_only ? {
      historyNumber: String(order.history_order_number), companyCode: String(order.CodigoEmpresa),
      sellerCode: String(order.CodigoVendedor), customerCode: String(order.CodigoCliente),
    } : undefined;
    let connected = false;
    const connectionTimeout = setTimeout(() => {
      if (active && !connected) {
        setError('Não foi possível abrir o editor. Feche esta janela e tente novamente.');
        setLoading(false);
      }
    }, 15000);
    const reply = (message: Record<string, unknown>) => {
      if (active)
        frame.current?.contentWindow?.postMessage({ protocol: PROTOCOL, channel, ...message }, origin);
    };
    const onMessage = async (event: MessageEvent) => {
      if (
        event.source !== frame.current?.contentWindow ||
        event.origin !== origin ||
        event.data?.protocol !== PROTOCOL ||
        event.data?.channel !== channel
      )
        return;
      const message = event.data;
      if (message.type === 'editor-ready' && bootstrap) {
        setLoading(false);
        return;
      }
      if (message.type === 'editor-error') {
        setError(typeof message.message === 'string' ? message.message : 'Não foi possível abrir o pedido.');
        setLoading(false);
        return;
      }
      if (message.type === 'ready') {
        connected = true;
        clearTimeout(connectionTimeout);
        try {
          initialization ||= getApi()
            .then((api) =>
              api.get(historyIdentity ? '/api/v1/orders/unified/history/view'
                : access === 'create' ? '/api/v1/orders/create/context'
                  : `/api/v1/orders/${encodeURIComponent(String(order?.NumeroPedido))}/${endpoint}`, {
                params: historyIdentity || (access === 'create'
                  ? { sellerCode } : { companyCode: String(order?.CodigoEmpresa) }),
              }),
            )
            .then((response) => {
              if (!response.data.success)
                throw new Error(response.data.message || 'Não foi possível abrir o pedido.');
              bootstrap = { ...response.data.data, access, initialStage, locale: i18n.language };
              return bootstrap;
            });
          reply({ type: 'initialize', bootstrap: await initialization });
        } catch (cause: any) {
          if (!active) return;
          const message =
            cause?.response?.data?.message || cause?.message || 'Não foi possível abrir o pedido.';
          setError(message);
          setLoading(false);
          reply({
            type: 'initialize-error',
            message,
          });
        }
        return;
      }
      if (message.type === 'dirty') {
        dirtyRef.current = Boolean(message.dirty);
        setDirty(dirtyRef.current);
        return;
      }
      if (message.type === 'request-close') {
        if (dirtyRef.current) setConfirmClose(true);
        else finishCloseRef.current();
        return;
      }
      if (message.type === 'saved' && access !== 'view') {
        dirtyRef.current = false;
        setDirty(false);
        approvalChanged.current = false;
        savedRef.current();
        closeRef.current();
        return;
      }
      if (message.type === 'approval-changed') {
        if (access === 'edit') approvalChanged.current = true;
        else if (access === 'view') savedRef.current();
        return;
      }
      if (
        message.type !== 'request' ||
        !bootstrap ||
        typeof message.id !== 'string' ||
        typeof message.path !== 'string'
      )
        return;
      try {
        const url = new URL(message.path, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Destino de consulta inválido.');
        const method = String(message.method).toUpperCase();
        if (method === 'GET' && url.pathname === '/api/v1/auth/user')
          url.searchParams.set('withPermissions', 'true');
        const body = typeof message.body === 'string' ? JSON.parse(message.body) : message.body;
        const orderPrefix = `/api/v1/orders/${encodeURIComponent(String(order?.NumeroPedido))}`;
        const prefix = historyIdentity ? '/api/v1/orders/unified/history/view/query'
          : access === 'create' ? '/api/v1/orders/create/query' : `${orderPrefix}/${endpoint}/query`;
        const approvalPath = `${orderPrefix}/approval`;
        const approvalId = url.pathname.startsWith(`${approvalPath}/`) ? url.pathname.slice(approvalPath.length + 1) : '';
        const allowedApproval = !historyIdentity && access !== 'create' && (
          (method === 'GET' && url.pathname === approvalPath
            && url.searchParams.get('companyCode') === String(order?.CodigoEmpresa))
          || (method === 'PUT' && /^\d+$/.test(approvalId)
            && body?.companyCode === String(order?.CodigoEmpresa)));
        const download = /^\/api\/v1\/tenant-custom-libs\/version-download\/(\d+)$/.exec(url.pathname);
        const allowedDownload =
          download &&
          method === 'GET' &&
          bootstrap.modules.some((entry: any) => String(entry.id) === download[1]);
        const allowed =
          (method === 'GET' && url.pathname === '/api/v1/auth/user') ||
          (method === 'POST' &&
            url.pathname === prefix &&
            (historyIdentity ? Object.entries(historyIdentity).every(([key, value]) => body?.[key] === value)
              : access === 'create' ? body?.sellerCode === sellerCode
                : body?.companyCode === String(order?.CodigoEmpresa))) ||
          (access === 'edit' && method === 'POST' &&
            url.pathname === '/api/v1/orders' &&
            String(body?.NumeroPedido) === String(order?.NumeroPedido) &&
            String(body?.CodigoEmpresa) === String(order?.CodigoEmpresa)) ||
          (access === 'create' && method === 'POST' && url.pathname === '/api/v1/orders'
            && body?.NumeroPedido == null && body?.CodigoVendedor === sellerCode
            && body?._creator?.sellerCode === sellerCode) || allowedDownload || allowedApproval;
        if (!allowed) throw new Error('A operação está fora do contexto deste pedido.');
        const execute = async () => {
          const api = await getApi();
          const response = await api.request({
            url: url.pathname + url.search,
            method,
            data: body,
            ...(allowedDownload
              ? { responseType: 'text', transformResponse: [(value: unknown) => value] }
              : {}),
          });
          return {
            body: allowedDownload ? String(response.data) : JSON.stringify(response.data),
            status: response.status,
            contentType: String(response.headers['content-type'] || 'application/json'),
          };
        };
        let response;
        if (allowedDownload) {
          const key = String(download![1]);
          let cached = moduleCache.get(key);
          if (!cached) {
            if (moduleCache.size >= 128) moduleCache.delete(moduleCache.keys().next().value as string);
            cached = execute();
            moduleCache.set(key, cached);
            void cached.catch(() => moduleCache.delete(key));
          }
          response = await cached;
        } else response = await execute();
        reply({ type: 'response', id: message.id, ...response });
      } catch (cause: any) {
        if (cause?.response)
          reply({
            type: 'response',
            id: message.id,
            status: cause.response.status,
            body: JSON.stringify(cause.response.data),
            contentType: 'application/json',
          });
        else
          reply({
            type: 'response',
            id: message.id,
            error: cause?.message || 'Não foi possível concluir a operação.',
          });
      }
    };
    window.addEventListener('message', onMessage);
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      active = false;
      clearTimeout(connectionTimeout);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [source, channel, order?.NumeroPedido, order?.CodigoEmpresa, order?.history_order_number, order?.history_only,
    order?.CodigoVendedor, order?.CodigoCliente, access, initialStage, sellerCode]);

  return (
    <>
      <Modal
        showModal
        maximized
        title={access === 'create' ? i18n.t('newOrder')
          : `${access === 'view' ? 'Visualizar' : 'Editar'} pedido ${displayNumber}`}
        hideActionButton
        hideFooter
        hideHeader
        closeModal={requestClose}
        style={{ height: '100dvh', minHeight: 0 }}
        bodyStyle={{
          position: 'relative',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          background: 'inherit',
        }}
      >
        {error ? (
          <FeedbackContainer>
            <FeedbackCard>
              <p role="alert">{error}</p>
              <Btn text={i18n.t('close')} onClick={requestClose} style={{ margin: '0 auto' }} />
            </FeedbackCard>
          </FeedbackContainer>
        ) : (
          source && (
            <iframe
              ref={frame}
              src={source}
              title={access === 'create' ? i18n.t('newOrder')
                : `${access === 'view' ? 'Visualização' : 'Edição'} do pedido ${displayNumber}`}
              referrerPolicy="origin"
              onLoad={() =>
                frame.current?.contentWindow?.postMessage(
                  { protocol: PROTOCOL, channel, type: 'host-ready' },
                  new URL(source).origin,
                )
              }
              style={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                border: 0,
                display: 'block',
                visibility: loading ? 'hidden' : 'visible',
              }}
            />
          )
        )}
        {loading && !error && (
          <div
            role="status"
            aria-label="Carregando pedido"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 24,
              background: 'inherit',
            }}
          >
            <AnimatedLoadingIcon />
            <span>Carregando pedido…</span>
            <Btn text={i18n.t('close')} onClick={requestClose} />
          </div>
        )}
      </Modal>
      {confirmClose && (
        <Modal
          showModal
          title="Descartar alterações?"
          closeModal={() => setConfirmClose(false)}
          action={finishClose}
          actionNameBtn="Descartas"
          cancelButton={() => setConfirmClose(false)}
        >
          <div style={{ padding: '1rem' }}>
            As alterações deste pedido ainda não foram salvas. Ao fechar, elas serão descartadas.
          </div>
        </Modal>
      )}
    </>
  );
}
